import {useCallback, useEffect} from "react";
import {useFilePicker} from "use-file-picker";
import {IFilePickerButtonProps} from "./FilePickerButton.interfaces";
import {
    AddIcon,
    Button,
    ExcelColorIcon,
    FilesCodeIcon,
    FilesEmptyIcon,
    FilesHtmlColoredIcon,
    FilesPdfIcon,
    FilesPictureColoredIcon,
    FilesSoundIcon,
    FilesTxtIcon,
    FilesVideoIcon,
    FilesZipIcon,
    Flex,
    FormLabel,
    OneNoteColorIcon,
    PowerPointColorIcon,
    TrashCanIcon,
    WordColorIcon
} from "@fluentui/react-northstar";

export const FilePickerButton = (props: IFilePickerButtonProps) => {
    const {filesContent, openFilePicker, loading, clear} = useFilePicker({
        multiple: false,
        readAs: 'DataURL',
        accept: props.formats ?? [],
    });

    useEffect(function onFileSelected() {
        (async () => {
            if (filesContent.length !== 1) return;
            const file = filesContent[0];
            if (!file) throw new Error("File is undefined");
            props.handleFileSelected(file);
        })();
    }, [filesContent]);

    const handleClearFile = useCallback(async () => {
        clear();
        props.handleFileSelected(undefined);
    }, [clear]);

    const getItemIcon = useCallback((name: string) => {
        const extension = name.split(".")[1]?.toLowerCase() ?? "";
        switch (extension) {
            case "docx":
                return <WordColorIcon/>;
            case "xlsx":
                return <ExcelColorIcon/>;
            case "oneNote":
                return <OneNoteColorIcon/>;
            case "pptx":
                return <PowerPointColorIcon/>;
            case "txt":
                return <FilesTxtIcon/>;
            case "pdf":
                return <FilesPdfIcon/>;
            case "html":
                return <FilesHtmlColoredIcon/>;
            default:
                break;
        }
        const codeExtensions = ["js", "ts", "tsx", "css", "py", "c", "cpp", "hpp", "cgi", "pl", "class", "cs", "h", "java", "php", "sh", "swift", "vb"];
        if (codeExtensions.includes(extension)) return <FilesCodeIcon/>;
        const archiveExtensions = ["zip", "rar", "tar", "7z", "gzip"];
        if (archiveExtensions.includes(extension)) return <FilesZipIcon/>;
        const imageExtensions = ["jpg", "png", "heic", "heif", "jpeg", "gif", "tiff", "raw", "svg", "bmp"];
        if (imageExtensions.includes(extension)) return <FilesPictureColoredIcon/>;
        const soundExtensions = ["mp3", "wav", "aiff", "flac", "aac"];
        if (soundExtensions.includes(extension)) return <FilesSoundIcon/>;
        const videoExtensions = ["mp4", "avi", "mov", "webm", "mkv", "flv", "wmv", "mpeg", "m4v", "3gp"];
        if (videoExtensions.includes(extension)) return <FilesVideoIcon/>;
        return <FilesEmptyIcon/>;
    }, []);

    const buttonIcon = props.buttonIcon ?? (!props.value ? <AddIcon/> : getItemIcon(props.value.name));

    return (
        <Flex column className={"form-input"}>
            <FormLabel content={props.label}/>
            <Flex gap={"gap.smaller"}>
                <Button
                    fluid
                    content={!props.value ? props.buttonLabel : props.value.name}
                    icon={buttonIcon}
                    onClick={openFilePicker}
                    loading={loading}
                    disabled={loading}
                />
                {!props.value ? null : (
                    <Button
                        iconOnly
                        text
                        loading={loading}
                        disabled={loading}
                        icon={<TrashCanIcon outline/>}
                        onClick={handleClearFile}
                    />
                )}
            </Flex>
        </Flex>
    )
}